import React, {useRef} from 'react';
import {Checkbox, type CheckboxProps} from '@mui/material';
import {type CellContext, type RowData} from '@tanstack/react-table';
import {useArrowFocusTrapItem} from '@netx/core-components/ArrowFocusTrapNext';
import {i18n} from '@netx/core-next/locales/i18n-instance';
import {stopPropagation} from '@netx/core-next/utils/browser';
import {splitEventHandler} from '@netx/core-next/utils/fn';

export interface SelectionCellProps<TData extends RowData> extends CellContext<
	TData,
	any
> {
	CheckboxProps?: Partial<CheckboxProps>;
}

export const SelectionCellPrimative = <Tdata extends RowData>({
	row,
	CheckboxProps,
}: SelectionCellProps<Tdata>) => {
	const shiftKeyUsed = useRef(false);
	const arrowFocusTrapItem = useArrowFocusTrapItem({
		index: row.index,
	});

	const disabled = !row.getCanSelect();

	return (
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
		<div
			onMouseDown={stopPropagation}
			style={{cursor: disabled ? 'not-allowed' : undefined}}
		>
			<Checkbox
				title={i18n.t('i18n.select')}
				checked={row.getIsSelected()}
				disabled={disabled}
				tabIndex={arrowFocusTrapItem.attributes.tabIndex}
				{...CheckboxProps}
				onBlur={splitEventHandler(
					CheckboxProps?.onBlur,
					arrowFocusTrapItem.actionAttributes.onBlur,
				)}
				onChange={splitEventHandler(CheckboxProps?.onChange, (event) => {
					row.toggleSelected(event.target.checked, {
						// @ts-ignore
						shiftKey: shiftKeyUsed.current,
					});
				})}
				onClick={splitEventHandler(CheckboxProps?.onClick, (event) => {
					stopPropagation(event);
					shiftKeyUsed.current = event.shiftKey;
					arrowFocusTrapItem.setActive();
				})}
				onFocus={splitEventHandler(
					CheckboxProps?.onFocus,
					arrowFocusTrapItem.actionAttributes.onFocus,
				)}
			/>
		</div>
	);
};

export const SelectionCell = SelectionCellPrimative;

export default SelectionCell;
